import { VariantProps, cva } from 'class-variance-authority';
import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';

import { TooltipProvider, TooltipContent, TooltipTrigger, Tooltip } from '../tooltip/tooltip';
import { SheetContent, SheetTitle, Sheet } from '../sheet/sheet';
import { Separator } from '../separator/separator';
import { useMediaQuery } from '../../hooks';
import { Button } from '../button/button';
import { cn } from '../../utils';
import { Menu } from '../icons';

const SIDEBAR_KEYBOARD_SHORTCUT = 'b';
const SIDEBAR_WIDTH_MOBILE = '15rem';
const SIDEBAR_WIDTH_ICON = '3.5rem';
const SIDEBAR_WIDTH = '15.5rem';

interface SidebarContext {
  setOpenMobile: (open: boolean) => void;
  setOpen: (open: boolean) => void;
  state: 'collapsed' | 'expanded';
  toggleSidebar: () => void;
  openMobile: boolean;
  isMobile: boolean;
  open: boolean;
}

// eslint-disable-next-line no-redeclare
const SidebarContext = React.createContext<SidebarContext | null>(null);

function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider.');
  }

  return context;
}

const SidebarProvider = React.forwardRef<
  HTMLDivElement,
  {
    onOpenChange?: (open: boolean) => void;
    defaultOpen?: boolean;
    open?: boolean;
  } & React.ComponentProps<'div'>
>(
  (
    {
      defaultOpen = true,
      open: openProp,
      onOpenChange: setOpenProp,
      className,
      style,
      children,
      ...props
    },
    ref,
  ) => {
    const isMobile = useMediaQuery('MOBILE');
    const [openMobile, setOpenMobile] = React.useState(false);

    // This is the internal state of the sidebar.
    // We use openProp and setOpenProp for control from outside the component.
    const [_open, _setOpen] = React.useState(defaultOpen);
    const open = openProp ?? _open;
    const setOpen = React.useCallback(
      (value: ((value: boolean) => boolean) | boolean) => {
        const openState = typeof value === 'function' ? value(open) : value;
        if (setOpenProp) setOpenProp(openState);
        else _setOpen(openState);
      },
      [setOpenProp, open],
    );

    // Helper to toggle the sidebar.
    const toggleSidebar = React.useCallback(() => {
      return isMobile ? setOpenMobile((open) => !open) : setOpen((open) => !open);
    }, [isMobile, setOpen, setOpenMobile]);

    // Adds a keyboard shortcut to toggle the sidebar.
    React.useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
          event.preventDefault();
          toggleSidebar();
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, [toggleSidebar]);

    // We add a state so that we can do data-state="expanded" or "collapsed".
    // This makes it easier to style the sidebar with Tailwind classes.
    const state = open ? 'expanded' : 'collapsed';

    const contextValue = React.useMemo<SidebarContext>(
      () => ({
        state,
        open,
        setOpen,
        isMobile,
        openMobile,
        setOpenMobile,
        toggleSidebar,
      }),
      [state, open, setOpen, isMobile, openMobile, setOpenMobile, toggleSidebar],
    );

    return (
      <SidebarContext.Provider value={contextValue}>
        <TooltipProvider delayDuration={0}>
          <div
            className={cn(
              // FIXME: `container` should be here?
              'group/sidebar-wrapper flex min-h-svh w-full relative mx-auto',
              { 'pl-6': !isMobile },
              className,
            )}
            style={
              {
                '--sidebar-width-icon': SIDEBAR_WIDTH_ICON,
                '--sidebar-width': SIDEBAR_WIDTH,
                ...style,
              } as React.CSSProperties
            }
            ref={ref}
            {...props}
          >
            {children}
          </div>
        </TooltipProvider>
      </SidebarContext.Provider>
    );
  },
);
SidebarProvider.displayName = 'SidebarProvider';

const Sidebar = React.forwardRef<
  HTMLDivElement,
  {
    collapsible?: 'offcanvas' | 'icon' | 'none';
    variant?: 'floating' | 'sidebar';
    side?: 'right' | 'left';
  } & React.ComponentProps<'div'>
>(
  (
    {
      side = 'left',
      variant = 'sidebar',
      collapsible = 'offcanvas',
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const { isMobile, state, openMobile, setOpenMobile } = useSidebar();

    if (collapsible === 'none') {
      return (
        <div
          className={cn(
            'flex h-full w-[var(--sidebar-width)] flex-col bg-background text-weak',
            className,
          )}
          ref={ref}
          {...props}
        >
          {children}
        </div>
      );
    }

    if (isMobile) {
      return (
        <Sheet onOpenChange={setOpenMobile} open={openMobile} {...props}>
          <SheetContent
            style={
              {
                '--sidebar-width': SIDEBAR_WIDTH_MOBILE,
              } as React.CSSProperties
            }
            className="w-[var(--sidebar-width)] bg-background p-0 text-weak [&>button]:hidden"
            data-sidebar="sidebar"
            data-mobile="true"
            side={side}
          >
            <SheetTitle className="sr-only">Menu</SheetTitle>
            <div className="flex h-full w-full flex-col">{children}</div>
          </SheetContent>
        </Sheet>
      );
    }

    return (
      <div
        data-collapsible={state === 'collapsed' ? collapsible : ''}
        className="bg-body group peer hidden text-weak md:block"
        data-variant={variant}
        data-state={state}
        data-side={side}
        ref={ref}
      >
        {/* This is what handles the sidebar gap on desktop */}
        <div
          className={cn(
            'relative h-svh w-[var(--sidebar-width)] bg-transparent transition-[width] duration-200 ease-linear',
            'group-data-[collapsible=offcanvas]:w-0',
            'group-data-[side=right]:rotate-180',
            variant === 'floating'
              ? 'group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4))]'
              : 'group-data-[collapsible=icon]:w-[var(--sidebar-width-icon)]',
          )}
        />
        <div
          className={cn(
            'absolute inset-y-0 z-10 hidden w-[var(--sidebar-width)] transition-[left,right,width] duration-200 ease-linear md:flex',
            side === 'left'
              ? 'left-auto group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]'
              : 'right-auto group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]',
            // Adjust the padding for floating
            variant === 'floating'
              ? 'my-8 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.2)_+2px)]'
              : 'group-data-[collapsible=icon]:w-[var(--sidebar-width-icon)] group-data-[side=left]:border-r group-data-[side=right]:border-l border-border-default',
            className,
          )}
          {...props}
        >
          <div
            className="flex h-full w-full flex-col bg-background group-data-[variant=floating]:rounded-xl shadow-lg"
            data-sidebar="sidebar"
          >
            {children}
          </div>
        </div>
      </div>
    );
  },
);
Sidebar.displayName = 'Sidebar';

const SidebarTrigger = React.forwardRef<
  React.ElementRef<typeof Button>,
  React.ComponentProps<typeof Button>
>(({ onClick, ...props }, ref) => {
  const { toggleSidebar } = useSidebar();

  return (
    <Button
      onClick={(event) => {
        onClick?.(event);
        toggleSidebar();
      }}
      data-sidebar="trigger"
      variant="ghost"
      size="small"
      ref={ref}
      icon
      {...props}
    >
      <Menu />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  );
});
SidebarTrigger.displayName = 'SidebarTrigger';

const SidebarHeader = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(
  ({ className, ...props }, ref) => {
    return (
      <div
        className={cn('flex flex-col gap-2 p-5', className)}
        data-sidebar="header"
        ref={ref}
        {...props}
      />
    );
  },
);
SidebarHeader.displayName = 'SidebarHeader';

const SidebarFooter = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(
  ({ className, ...props }, ref) => {
    return (
      <div
        className={cn('flex flex-col gap-2 p-3', className)}
        data-sidebar="footer"
        ref={ref}
        {...props}
      />
    );
  },
);
SidebarFooter.displayName = 'SidebarFooter';

const SidebarSeparator = React.forwardRef<
  React.ElementRef<typeof Separator>,
  React.ComponentProps<typeof Separator>
>(({ className, ...props }, ref) => {
  return (
    <Separator
      className={cn('mx-2 w-auto bg-border-default', className)}
      data-sidebar="separator"
      ref={ref}
      {...props}
    />
  );
});
SidebarSeparator.displayName = 'SidebarSeparator';

const SidebarContent = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(
  ({ className, ...props }, ref) => {
    return (
      <div
        className={cn(
          'flex min-h-0 flex-1 flex-col gap-2 overflow-auto group-data-[collapsible=icon]:overflow-auto group-data-[collapsible=icon]:[direction:rtl]',
          className,
        )}
        data-sidebar="content"
        ref={ref}
        {...props}
      />
    );
  },
);
SidebarContent.displayName = 'SidebarContent';

const SidebarGroup = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(
  ({ className, ...props }, ref) => {
    return (
      <div
        className={cn('relative flex w-full min-w-0 flex-col p-3', className)}
        data-sidebar="group"
        ref={ref}
        {...props}
      />
    );
  },
);
SidebarGroup.displayName = 'SidebarGroup';

const SidebarGroupLabel = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & { asChild?: boolean }
>(({ className, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'div';

  return (
    <Comp
      className={cn(
        'flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium text-weak/70 outline-none ring-border-sky transition-[margin,opa] duration-200 ease-linear focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0',
        'group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0',
        className,
      )}
      data-sidebar="group-label"
      ref={ref}
      {...props}
    />
  );
});
SidebarGroupLabel.displayName = 'SidebarGroupLabel';

const SidebarGroupContent = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(
  ({ className, ...props }, ref) => (
    <div
      className={cn('w-full text-sm', className)}
      data-sidebar="group-content"
      ref={ref}
      {...props}
    />
  ),
);
SidebarGroupContent.displayName = 'SidebarGroupContent';

const SidebarMenu = React.forwardRef<HTMLUListElement, React.ComponentProps<'ul'>>(
  ({ className, ...props }, ref) => (
    <ul
      className={cn('flex w-full min-w-0 flex-col gap-4', className)}
      data-sidebar="menu"
      ref={ref}
      {...props}
    />
  ),
);
SidebarMenu.displayName = 'SidebarMenu';

const SidebarMenuItem = React.forwardRef<HTMLLIElement, React.ComponentProps<'li'>>(
  ({ className, ...props }, ref) => (
    <li
      className={cn('group/menu-item relative', className)}
      data-sidebar="menu-item"
      ref={ref}
      {...props}
    />
  ),
);
SidebarMenuItem.displayName = 'SidebarMenuItem';

const sidebarMenuButtonVariants = cva(
  `peer/menu-button flex w-full items-center gap-4 overflow-hidden rounded-md p-2 px-3 text-left outline-none font-medium
   ring-border-sky transition-[width,height,padding] hover:bg-surface-hover hover:text-surface-hover focus-visible:ring-2
   active:bg-surface-hover active:text-default disabled:pointer-events-none disabled:opacity-50 group-has-[[data-sidebar=menu-action]]/menu-item:pr-8
   aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-body data-[active=true]:font-semibold
   data-[active=true]:text-default data-[state=open]:hover:bg-surface-hover data-[state=open]:hover:text-surface-hover
   group-data-[collapsible=icon]:!size-10 group-data-[collapsible=icon]:!p-3 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0`,
  {
    variants: {
      variant: { default: 'hover:bg-surface-hover hover:text-default' },
      size: { default: 'h-10 text-sm' },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

const SidebarMenuButton = React.forwardRef<
  HTMLButtonElement,
  {
    tooltip?: React.ComponentProps<typeof TooltipContent> | string;
    isActive?: boolean;
    asChild?: boolean;
  } & VariantProps<typeof sidebarMenuButtonVariants> &
    React.ComponentProps<'button'>
>(
  (
    {
      asChild = false,
      isActive = false,
      variant = 'default',
      size = 'default',
      className,
      tooltip,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    const { isMobile, state } = useSidebar();

    const button = (
      <Comp
        className={cn(sidebarMenuButtonVariants({ variant, size }), className)}
        data-sidebar="menu-button"
        data-active={isActive}
        data-size={size}
        ref={ref}
        {...props}
      />
    );

    if (!tooltip) {
      return button;
    }

    if (typeof tooltip === 'string') {
      tooltip = {
        children: tooltip,
      };
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent
          hidden={state !== 'collapsed' || isMobile}
          align="center"
          side="right"
          {...tooltip}
        />
      </Tooltip>
    );
  },
);
SidebarMenuButton.displayName = 'SidebarMenuButton';

const SidebarMenuBadge = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(
  ({ className, ...props }, ref) => (
    <div
      className={cn(
        'pointer-events-none absolute right-1 flex h-5 min-w-5 select-none items-center justify-center rounded-md px-1 text-xs font-medium tabular-nums text-weak',
        'peer-hover/menu-button:text-surface-hover peer-data-[active=true]/menu-button:text-surface-hover',
        'peer-data-[size=default]/menu-button:top-2.5',
        'group-data-[collapsible=icon]:hidden',
        className,
      )}
      data-sidebar="menu-badge"
      ref={ref}
      {...props}
    />
  ),
);
SidebarMenuBadge.displayName = 'SidebarMenuBadge';

export {
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenuButton,
  SidebarMenuBadge,
  SidebarSeparator,
  SidebarMenuItem,
  SidebarProvider,
  SidebarContent,
  SidebarTrigger,
  SidebarFooter,
  SidebarHeader,
  SidebarGroup,
  SidebarMenu,
  useSidebar,
  Sidebar,
};
